/*
 * Copyright © 2017 - 2019 Atilika Inc. All rights reserved.
 */

import React, {memo} from "react";
import {Language} from "../common/Language";
import KuromojiDemoForm from "./KuromojiDemoForm";
import {Mode, Token, tokenize} from "./Kuromoji";
import KuromojiDemoTokens from "./KuromojiDemoTokens";
import KuromojiDemoViterbi from "./KuromojiDemoViterbi";

interface KuromojiStateProps {
    text: string;
    mode: Mode;
    isProcessing: boolean;
    tokens: Token[];
    viterbiGraph?: string;
    error?: string;
}

interface KuromojiDemoProps {
    lang: Language;
}

const errors = {
    ja: {
        internal: "エラーが発生しました。",
        truncate: "テキストは512文字まで限られています。",
        viterbiTruncate: "ビタビの場合、テキストは３２文字まで限られています。"
    },
    en: {
        internal: "We encountered an error processing your request. Please try again later",
        truncate: "Only the first 512 characters will be processed.",
        viterbiTruncate: "When using viterbi mode, only the first 32 characters are processed."
    }
};

class KuromojiDemo extends React.PureComponent<KuromojiDemoProps, KuromojiStateProps> {
    public state: KuromojiStateProps = {
        isProcessing: false,
        mode: Mode.Normal,
        text: "",
        tokens: []
    };

    public render() {
        return (
            <div>
                <KuromojiDemoForm
                    error={this.state.error}
                    text={this.state.text}
                    mode={this.state.mode}
                    isProcessing={this.state.isProcessing}
                    lang={this.props.lang}
                    onModeChange={this.handleModeChange}
                    onTextChange={this.handleTextChange}
                    onSubmit={this.submit}
                    onClear={this.handleClear}
                />
                <KuromojiDemoTokens tokens={this.state.tokens} mode={this.state.mode} />
                <KuromojiDemoViterbi graph={this.state.viterbiGraph} />
            </div>
        );
    }

    private handleTextChange = (text: string) => {
        if (this.state.mode === Mode.Viterbi && text.length > 32) {
            this.setState({text, error: errors[this.props.lang].viterbiTruncate});
        } else if (text.length > 512) {
            this.setState({text, error: errors[this.props.lang].truncate});
        } else {
            this.setState({text, error: undefined});
        }
    };

    private handleModeChange = (mode: Mode) => {
        this.setState({mode}, () => {
            if (this.state.text.length < 1) {
                return;
            }
            this.submit();
        });
    };

    private handleClear = () => {
        if (this.state.isProcessing) {
            return;
        }

        this.setState({
            text: "",
            error: undefined,
            mode: Mode.Normal,
            tokens: [],
            viterbiGraph: undefined
        });
    };

    private submit = () => {
        if (this.state.isProcessing || this.state.text.length < 1) {
            return;
        }

        this.setState({isProcessing: true, viterbiGraph: undefined}, () => {
            tokenize(this.state.text, this.state.mode)
                .then(({tokens, viterbiGraph}) => {
                    this.setState({
                        isProcessing: false,
                        error: undefined,
                        tokens,
                        viterbiGraph
                    });
                })
                .catch(() => {
                    this.setState({isProcessing: false, error: errors[this.props.lang].internal});
                });
        });
    };
}

export default memo(KuromojiDemo);
